import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useDarkMode } from "./contexts/DarkModeContext";
import { 
  Sun, Moon, LogIn, User, Lock, ArrowRight, 
  CheckCircle2, AlertCircle, Loader2,
  Building2, Users, UserPlus, RefreshCw, Clock,
  Check, ChevronDown, ChevronUp, LogOut, X, AlertTriangle
} from "lucide-react";

const getApiUrl = (path) => {
  const isFrontendDevServer = ['3000', '1234', '5173'].includes(window.location.port);
  const devBase = isFrontendDevServer
    ? `${window.location.protocol}//${window.location.hostname}:3001`
    : '';
  return `${devBase}${path}`;
};

const getWsUrl = (path) => {
  const isFrontendDevServer = ['3000', '1234', '5173'].includes(window.location.port);
  const host = isFrontendDevServer ? `${window.location.hostname}:3001` : window.location.host;
  const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${protocol}://${host}${path}`;
};

function Login() {
  const { darkMode, toggleDarkMode } = useDarkMode();
  const [account, setAccount] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [showNotification, setShowNotification] = React.useState(false);
  const [accountFocus, setAccountFocus] = React.useState(false);
  const [passwordFocus, setPasswordFocus] = React.useState(false);
  const navigate = useNavigate();
  
  const accountRef = React.useRef(null);
  const passwordRef = React.useRef(null);

  // 工作室狀態
  const [studioStatuses, setStudioStatuses] = React.useState([]);
  const [studioPersonnel, setStudioPersonnel] = React.useState({ regularPersonnel: [], companions: [] });
  const [studioLoading, setStudioLoading] = React.useState(false);
  const [tempEntryName, setTempEntryName] = React.useState('');
  const [showStudioSection, setShowStudioSection] = React.useState(true);
  const [confirmAction, setConfirmAction] = React.useState(null);
  const wsRef = React.useRef(null);

  const STUDIO_API = '';

  const fetchStatus = React.useCallback(async () => {
    try {
      setStudioLoading(true);
      const res = await fetch(getApiUrl(`${STUDIO_API}/api/studio/status`));
      if (res.ok) setStudioStatuses(await res.json());
    } catch (e) {} finally { setStudioLoading(false); }
  }, []);

  const fetchPersonnel = React.useCallback(async () => {
    try {
      const res = await fetch(getApiUrl(`${STUDIO_API}/api/studio/personnel`));
      if (res.ok) setStudioPersonnel(await res.json());
    } catch (e) {}
  }, []);

  // 初始加載 + 定期輪詢備份
  React.useEffect(() => {
    fetchStatus();
    fetchPersonnel();
    const pollInterval = setInterval(() => {
      fetchStatus();
      fetchPersonnel();
    }, 30000);
    return () => clearInterval(pollInterval);
  }, [fetchStatus, fetchPersonnel]);

  // WebSocket 即時更新 + 頁面可見/網絡恢復時立即刷新
  React.useEffect(() => {
    let ws, reconnectTimer;
    const connectWs = () => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
        return;
      }
      try {
        const wsUrl = getWsUrl('/ws');
        ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            if (message.type === 'revenueUpdate') {
              if (message.action === 'studioStatusUpdate') setStudioStatuses(message.data || []);
              else if (message.action === 'studioPersonnelUpdate') setStudioPersonnel(message.data || { regularPersonnel: [], companions: [] });
            }
          } catch (e) {}
        };
        ws.onclose = () => { reconnectTimer = setTimeout(connectWs, 3000); };
        ws.onerror = () => {};
      } catch (e) { reconnectTimer = setTimeout(connectWs, 3000); }
    };
    connectWs();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (!ws || ws.readyState !== WebSocket.OPEN) {
          connectWs();
        }
        fetchStatus();
        fetchPersonnel();
      }
    };

    const handleOnline = () => {
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        connectWs();
      }
      fetchStatus();
      fetchPersonnel();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    return () => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) ws.close();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [fetchStatus, fetchPersonnel]);

  // 臨時進入工作室
  const handleTempEntry = async (name) => {
    const personName = name || tempEntryName.trim();
    if (!personName) return;
    try {
      const res = await fetch(getApiUrl(`${STUDIO_API}/api/studio/checkin`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ personName, purpose: '臨時', companions: [], isTemporary: true })
      });
      if (res.ok) {
        setTempEntryName('');
      } else {
        const err = await res.json();
        alert(err.error || '進入失敗');
      }
    } catch (e) {
      alert('網絡錯誤，請重試');
    }
  };

  // 快捷選擇進入
  const handleQuickSelect = async (personName, type) => {
    try {
      const res = await fetch(getApiUrl(`${STUDIO_API}/api/studio/checkin`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ personName, purpose: type === 'regular' ? '工作' : '隨行', companions: [] })
      });
      if (!res.ok) {
        const err = await res.json();
        alert(err.error || '進入失敗');
      }
    } catch (e) {
      alert('網絡錯誤，請重試');
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'heading': return '前往中';
      case 'in_studio': return '工作室中';
      case 'leaving': return '離開中';
      default: return status;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'heading': return darkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700';
      case 'in_studio': return darkMode ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700';
      case 'leaving': return darkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-700';
      default: return darkMode ? 'bg-gray-500/20 text-gray-400' : 'bg-gray-100 text-gray-600';
    }
  };

  const activePersonNames = studioStatuses.map(s => s.personName);
  const companionCountFn = (entries) => entries.reduce((sum, s) => sum + (s.companions?.length || 0), 0);
  const inStudioEntries = studioStatuses.filter(s => s.status === 'in_studio');
  const headingEntries = studioStatuses.filter(s => s.status === 'heading');
  const leavingEntries = studioStatuses.filter(s => s.status === 'leaving');
  const inStudioCount = inStudioEntries.length + companionCountFn(inStudioEntries);
  const headingCount = headingEntries.length + companionCountFn(headingEntries);
  const leavingCount = leavingEntries.length + companionCountFn(leavingEntries);

  // 更豐富的蘋果風格動畫效果
  const springTransition = {
    type: "spring",
    stiffness: 350,
    damping: 30,
  };

  const buttonVariants = {
    hover: { 
      scale: 1.03, 
      boxShadow: darkMode 
        ? "0 10px 25px rgba(0, 0, 0, 0.2)" 
        : "0 10px 25px rgba(0, 0, 0, 0.1)" 
    },
    tap: { scale: 0.97 },
  };

  const inputVariants = {
    focus: { 
      scale: 1.02,
      boxShadow: darkMode 
        ? "0 0 0 2px rgba(75, 85, 99, 0.3)" 
        : "0 0 0 2px rgba(59, 130, 246, 0.3)"
    }
  };
  
  // 頁面進入/退出動畫
  const pageVariants = {
    initial: { 
      opacity: 0,
      scale: 0.98,
      y: 10,
      filter: "blur(8px)"
    },
    animate: { 
      opacity: 1, 
      scale: 1, 
      y: 0,
      filter: "blur(0px)",
      transition: { 
        duration: 0.5,
        ...springTransition
      }
    },
    exit: { 
      opacity: 0, 
      y: -20,
      transition: { duration: 0.3 }
    }
  };

  const handleDarkModeToggle = toggleDarkMode;

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    
    try {
      const response = await fetch(getApiUrl("/api/login"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ account, password }),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem("token", data.token);
        
        // 儲存用戶資訊到 localStorage
        const userInfo = data.user || {};
        // 確保帳戶名稱也被儲存
        userInfo.account = account;
        localStorage.setItem("user", JSON.stringify(userInfo));
        
        setIsLoading(false);
        setShowNotification(true);
        
        setTimeout(() => {
          // 根據用戶名稱決定重定向路徑
          const accountLower = account.toLowerCase();
          if (accountLower === "uber" || accountLower === "shing" || accountLower === "ray" || accountLower === "fei" || accountLower === "althea") {
            navigate("/uber-account");
          } else {
            navigate("/dashboard");
          }
        }, 1500);
      } else {
        setIsLoading(false);
        const errorData = await response.json();
        setError(errorData.message || "登入失敗 Login Failed");
        
        // 添加輸入框抖動效果
        if (passwordRef.current) {
          passwordRef.current.classList.add("shake");
          setTimeout(() => {
            if (passwordRef.current) {
              passwordRef.current.classList.remove("shake");
            }
          }, 500);
        }
      }
    } catch (err) {
      setIsLoading(false);
      setError("無法連接到伺服器 Unable to connect to the server");
    }
  };

  return (
    <div
      className={`relative flex flex-col items-center justify-center min-h-screen min-h-[100dvh] px-3 sm:px-4 py-6 sm:py-8 overflow-hidden ${
        darkMode 
          ? "bg-[#0a0a0a]" 
          : "bg-[#f5f5f7]"
      }`}
    >
      {/* 背景裝飾光暈 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute -top-[30%] -right-[20%] w-[60vw] h-[60vw] sm:w-[40vw] sm:h-[40vw] rounded-full blur-[100px] sm:blur-[120px] ${
          darkMode ? "bg-blue-600/[0.07]" : "bg-blue-400/[0.12]"
        }`} />
        <div className={`absolute -bottom-[20%] -left-[15%] w-[50vw] h-[50vw] sm:w-[35vw] sm:h-[35vw] rounded-full blur-[100px] sm:blur-[120px] ${
          darkMode ? "bg-indigo-500/[0.05]" : "bg-indigo-300/[0.10]"
        }`} />
        <div className={`absolute top-[40%] left-[50%] -translate-x-1/2 w-[40vw] h-[40vw] sm:w-[25vw] sm:h-[25vw] rounded-full blur-[80px] sm:blur-[100px] ${
          darkMode ? "bg-purple-600/[0.04]" : "bg-purple-300/[0.06]"
        }`} />
      </div>

      {/* 暗色模式切換按鈕 */}
      <motion.button
        onClick={handleDarkModeToggle}
        className={`fixed top-3 right-3 sm:top-6 sm:right-6 p-2.5 rounded-full z-40 backdrop-blur-md transition-all duration-300 ${
          darkMode 
            ? "bg-white/[0.06] hover:bg-white/[0.10] text-gray-100 border border-white/[0.08]" 
            : "bg-black/[0.04] hover:bg-black/[0.08] text-gray-800 border border-black/[0.06]"
        }`}
        whileHover={{ scale: 1.1, rotate: 5 }}
        whileTap={{ scale: 0.9, rotate: 0 }}
        transition={springTransition}
      >
        {darkMode ? (
          <Sun className="w-4 h-4 sm:w-5 sm:h-5 text-amber-300" />
        ) : (
          <Moon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-800" />
        )}
      </motion.button>

      {/* 登入卡片 */}
      <motion.div
        className={`relative w-full max-w-[calc(100vw-1.5rem)] sm:max-w-md rounded-2xl sm:rounded-3xl overflow-hidden backdrop-blur-xl ${
          darkMode 
            ? "bg-white/[0.04] border border-white/[0.08] shadow-[0_8px_32px_rgba(0,0,0,0.4)]" 
            : "bg-white/70 border border-white/60 shadow-[0_8px_32px_rgba(0,0,0,0.08)]"
        }`}
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        <div className="p-5 sm:p-8">
          <motion.div 
            className="text-center mb-6 sm:mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, ...springTransition }}
          >
            <div className={`inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-2xl mb-4 ${
              darkMode ? "bg-blue-500/10 border border-blue-500/20" : "bg-blue-50 border border-blue-100"
            }`}>
              <LogIn className={`w-5 h-5 sm:w-6 sm:h-6 ${darkMode ? "text-blue-400" : "text-blue-600"}`} />
            </div>
            <h2 className={`text-xl sm:text-2xl font-bold tracking-tight ${
              darkMode ? "text-white" : "text-gray-900"
            }`}>
              查詢系統
            </h2>
            <p className={`mt-1.5 text-xs sm:text-sm tracking-wide ${
              darkMode ? "text-gray-500" : "text-gray-400"
            }`}>
              Secure & Efficient Information Platform
            </p>
          </motion.div>

          <form onSubmit={handleLogin} className="space-y-4 sm:space-y-5">
            <AnimatePresence>
              {error && (
                <motion.div
                  className={`rounded-xl p-3 ${
                    darkMode ? "bg-red-500/10 text-red-300 border border-red-500/20" : "bg-red-50 text-red-500 border border-red-100"
                  } flex items-center space-x-2`}
                  initial={{ opacity: 0, y: -10, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: 'auto' }}
                  exit={{ opacity: 0, y: -10, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span className="text-xs sm:text-sm">{error}</span>
                </motion.div>
              )}
            </AnimatePresence>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, ...springTransition }}
            >
              <div className="relative">
                <label
                  className={`block text-xs sm:text-sm font-medium mb-1.5 ${
                    darkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  帳戶 Account
                </label>
                <div className={`relative group overflow-hidden rounded-xl ${
                  accountFocus 
                    ? `ring-2 ring-offset-0 ${darkMode ? "ring-blue-500/30" : "ring-blue-500/30"}` 
                    : ""
                }`}>
                  <span className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                    darkMode ? "text-blue-400/70" : "text-blue-500/70"
                  } z-10`}>
                    <User className="w-4 h-4 sm:w-5 sm:h-5" />
                  </span>
                  <motion.input
                    ref={accountRef}
                    type="text"
                    required
                    value={account}
                    onChange={(e) => setAccount(e.target.value)}
                    onFocus={() => setAccountFocus(true)}
                    onBlur={() => setAccountFocus(false)}
                    variants={inputVariants}
                    animate={accountFocus ? "focus" : ""}
                    className={`w-full pl-9 sm:pl-10 pr-4 py-3 sm:py-3.5 border rounded-xl outline-none text-sm sm:text-base ${
                      darkMode
                        ? "bg-white/[0.04] border-white/[0.08] text-white placeholder-gray-600"
                        : "bg-white/60 border-gray-200/60 text-gray-900 placeholder-gray-400"
                    } transition-all duration-200`}
                    placeholder="請輸入您的帳號"
                    style={{
                      boxShadow: `${accountFocus 
                        ? (darkMode ? "inset 0 0 0 1px rgba(59, 130, 246, 0.2)" : "inset 0 0 0 1px rgba(59, 130, 246, 0.2)") 
                        : "none"}`
                    }}
                  />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, ...springTransition }}
            >
              <div className="relative">
                <label
                  className={`block text-xs sm:text-sm font-medium mb-1.5 ${
                    darkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  密碼 Password
                </label>
                <div className={`relative group overflow-hidden rounded-xl ${
                  passwordFocus 
                    ? `ring-2 ring-offset-0 ${darkMode ? "ring-blue-500/30" : "ring-blue-500/30"}` 
                    : ""
                }`}>
                  <span className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                    darkMode ? "text-blue-400/70" : "text-blue-500/70"
                  } z-10`}>
                    <Lock className="w-4 h-4 sm:w-5 sm:h-5" />
                  </span>
                  <motion.input
                    ref={passwordRef}
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setPasswordFocus(true)}
                    onBlur={() => setPasswordFocus(false)}
                    variants={inputVariants}
                    animate={passwordFocus ? "focus" : ""}
                    className={`w-full pl-9 sm:pl-10 pr-4 py-3 sm:py-3.5 border rounded-xl outline-none text-sm sm:text-base ${
                      darkMode
                        ? "bg-white/[0.04] border-white/[0.08] text-white placeholder-gray-600"
                        : "bg-white/60 border-gray-200/60 text-gray-900 placeholder-gray-400"
                    } transition-all duration-200 shake-animation`}
                    placeholder="請輸入您的密碼"
                    style={{
                      boxShadow: `${passwordFocus 
                        ? (darkMode ? "inset 0 0 0 1px rgba(59, 130, 246, 0.2)" : "inset 0 0 0 1px rgba(59, 130, 246, 0.2)") 
                        : "none"}`
                    }}
                  />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, ...springTransition }}
              className="pt-1 sm:pt-2"
            >
              <motion.button
                type="submit"
                disabled={isLoading}
                className={`w-full py-3.5 sm:py-4 px-4 rounded-xl font-medium flex items-center justify-center space-x-2 text-sm sm:text-base ${
                  darkMode
                    ? "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white shadow-[0_4px_20px_rgba(59,130,246,0.25)]"
                    : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white shadow-[0_4px_20px_rgba(59,130,246,0.20)]"
                } transition-all duration-300 transform relative overflow-hidden`}
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <motion.div 
                  className="absolute inset-0 bg-white opacity-0 hover:opacity-10 transition-opacity duration-300"
                  whileHover={{ opacity: 0.15 }}
                  transition={{ duration: 0.3 }}
                />
                
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <span className="relative z-10">登入系統</span>
                    <motion.div
                      className="relative z-10"
                      animate={{ x: [0, 4, 0] }}
                      transition={{ 
                        repeat: Infinity, 
                        repeatType: "reverse", 
                        duration: 1.5,
                        ease: "easeInOut"
                      }}
                    >
                      <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                    </motion.div>
                  </>
                )}
              </motion.button>
            </motion.div>
          </form>
        </div>
      </motion.div>
      
      {/* 工作室快速進入 */}
      <motion.div
        className={`relative w-full max-w-[calc(100vw-1.5rem)] sm:max-w-md mt-4 sm:mt-6 rounded-2xl sm:rounded-3xl overflow-hidden backdrop-blur-xl ${
          darkMode 
            ? "bg-white/[0.04] border border-white/[0.08] shadow-[0_8px_32px_rgba(0,0,0,0.4)]" 
            : "bg-white/70 border border-white/60 shadow-[0_8px_32px_rgba(0,0,0,0.08)]"
        }`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, ...springTransition }}
      >
        <div className="p-4 sm:p-6">
          <button
            onClick={() => setShowStudioSection(!showStudioSection)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-2 sm:gap-2.5">
              <div className={`p-1.5 rounded-lg ${darkMode ? "bg-emerald-500/10 border border-emerald-500/20" : "bg-emerald-50 border border-emerald-100"}`}>
                <Building2 className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${darkMode ? "text-emerald-400" : "text-emerald-600"}`} />
              </div>
              <h3 className={`text-sm sm:text-base font-bold tracking-tight ${darkMode ? "text-white" : "text-gray-800"}`}>
                工作室
              </h3>
              <div className="flex items-center gap-1 ml-0.5 sm:ml-1">
                {inStudioCount > 0 && (
                  <span className={`text-[9px] sm:text-[10px] px-1.5 py-0.5 rounded-full font-medium ${darkMode ? 'bg-green-500/15 text-green-400 border border-green-500/20' : 'bg-green-50 text-green-700 border border-green-100'}`}>
                    {inStudioCount}人在內
                  </span>
                )}
                {headingCount > 0 && (
                  <span className={`text-[9px] sm:text-[10px] px-1.5 py-0.5 rounded-full font-medium ${darkMode ? 'bg-amber-500/15 text-amber-400 border border-amber-500/20' : 'bg-amber-50 text-amber-700 border border-amber-100'}`}>
                    {headingCount}人前往
                  </span>
                )}
                {studioStatuses.length === 0 && (
                  <span className={`text-[9px] sm:text-[10px] ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>目前無人</span>
                )}
              </div>
            </div>
            {showStudioSection ? (
              <ChevronUp className={`w-4 h-4 ${darkMode ? "text-gray-500" : "text-gray-400"}`} />
            ) : (
              <ChevronDown className={`w-4 h-4 ${darkMode ? "text-gray-500" : "text-gray-400"}`} />
            )}
          </button>

          <AnimatePresence>
            {showStudioSection && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className={`mt-3 sm:mt-4 pt-3 sm:pt-4 border-t space-y-3 sm:space-y-4 ${darkMode ? "border-white/[0.06]" : "border-gray-100"}`}>
                  {studioStatuses.length > 0 && (
                    <div className="space-y-1.5">
                      {studioStatuses.map(entry => (
                        <div 
                          key={entry.id}
                          className={`px-3 py-2 sm:py-2.5 rounded-xl ${
                            darkMode ? "bg-white/[0.03] border border-white/[0.05]" : "bg-gray-50/80 border border-gray-100"
                          }`}
                        >
                          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                            <span className={`text-xs sm:text-sm font-medium ${darkMode ? "text-gray-200" : "text-gray-700"}`}>
                              {entry.personName}
                            </span>
                            <span className={`text-[9px] sm:text-[10px] px-1.5 py-0.5 rounded-full font-medium ${getStatusBadge(entry.status)}`}>
                              {getStatusLabel(entry.status)}
                            </span>
                            {entry.purpose && (
                              <span className={`text-[9px] sm:text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                                darkMode ? "bg-white/[0.04] text-gray-500 border border-white/[0.06]" : "bg-gray-100/80 text-gray-500 border border-gray-200/60"
                              }`}>
                                {entry.purpose}
                              </span>
                            )}
                          </div>
                          {entry.companions && entry.companions.length > 0 && (
                            <div className="flex items-center gap-1 mt-1.5 ml-1 flex-wrap">
                              <UserPlus className={`w-3 h-3 flex-shrink-0 ${darkMode ? "text-gray-600" : "text-gray-400"}`} />
                              {entry.companions.map((comp, idx) => (
                                <span key={idx} className={`text-[9px] sm:text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                                  darkMode ? "bg-teal-500/8 text-teal-400 border border-teal-500/15" : "bg-teal-50 text-teal-600 border border-teal-100"
                                }`}>
                                  {comp.name}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <div>
                    <label className={`text-[10px] sm:text-xs font-medium mb-1.5 block tracking-wide ${darkMode ? "text-gray-500" : "text-gray-500"}`}>
                      臨時進入工作室
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={tempEntryName}
                        onChange={(e) => setTempEntryName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleTempEntry(); }}
                        placeholder="輸入人員名稱"
                        className={`flex-1 min-w-0 px-3 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm border outline-none transition-colors ${
                          darkMode 
                            ? "bg-white/[0.04] border-white/[0.08] text-white placeholder-gray-600 focus:border-emerald-500/40" 
                            : "bg-white/60 border-gray-200/60 text-gray-800 placeholder-gray-400 focus:border-emerald-500/60"
                        }`}
                      />
                      <motion.button
                        onClick={() => handleTempEntry()}
                        disabled={!tempEntryName.trim()}
                        className={`px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium transition-all flex-shrink-0 ${
                          !tempEntryName.trim()
                            ? (darkMode ? "bg-white/[0.04] text-gray-600 cursor-not-allowed border border-white/[0.06]" : "bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200")
                            : "bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-[0_2px_12px_rgba(16,185,129,0.25)]"
                        }`}
                        whileHover={tempEntryName.trim() ? { scale: 1.03 } : {}}
                        whileTap={tempEntryName.trim() ? { scale: 0.97 } : {}}
                      >
                        進入
                      </motion.button>
                    </div>
                  </div>

                  <div>
                    <label className={`text-[10px] sm:text-xs font-medium mb-2 block tracking-wide ${darkMode ? "text-gray-500" : "text-gray-500"}`}>
                      快捷選擇
                    </label>
                    
                    {studioPersonnel.regularPersonnel.length > 0 && (
                      <div className="mb-2.5">
                        <span className={`text-[9px] sm:text-[10px] font-semibold mb-1.5 block uppercase tracking-wider ${darkMode ? "text-emerald-500/70" : "text-emerald-600/70"}`}>
                          日常人員
                        </span>
                        <div className="flex flex-wrap gap-1.5">
                          {studioPersonnel.regularPersonnel.map(p => {
                            const isActive = activePersonNames.includes(p);
                            return (
                              <motion.button
                                key={p}
                                onClick={() => !isActive && setConfirmAction({ personName: p, type: 'regular' })}
                                disabled={isActive}
                                className={`px-2 sm:px-2.5 py-1 sm:py-1.5 rounded-lg text-[10px] sm:text-xs font-medium transition-all ${
                                  isActive
                                    ? (darkMode ? "bg-emerald-500/8 text-emerald-400/40 cursor-not-allowed border border-emerald-500/15" : "bg-emerald-50/60 text-emerald-400 cursor-not-allowed border border-emerald-100")
                                    : (darkMode ? "bg-white/[0.03] text-gray-300 hover:bg-emerald-500/15 hover:text-emerald-300 border border-white/[0.06] hover:border-emerald-500/25" : "bg-white/60 text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 border border-gray-200/60 hover:border-emerald-200")
                                }`}
                                whileHover={!isActive ? { scale: 1.05 } : {}}
                                whileTap={!isActive ? { scale: 0.95 } : {}}
                              >
                                {p}
                                {isActive && <span className="ml-1 opacity-50">·在線</span>}
                              </motion.button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {studioPersonnel.companions.length > 0 && (
                      <div>
                        <span className={`text-[9px] sm:text-[10px] font-semibold mb-1.5 block uppercase tracking-wider ${darkMode ? "text-teal-500/70" : "text-teal-600/70"}`}>
                          隨行人員
                        </span>
                        <div className="flex flex-wrap gap-1.5">
                          {studioPersonnel.companions.map(c => {
                            const isActive = activePersonNames.includes(c) || studioStatuses.some(s => (s.companions || []).some(comp => comp.name === c));
                            return (
                              <motion.button
                                key={c}
                                onClick={() => !isActive && setConfirmAction({ personName: c, type: 'companion' })}
                                disabled={isActive}
                                className={`px-2 sm:px-2.5 py-1 sm:py-1.5 rounded-lg text-[10px] sm:text-xs font-medium transition-all ${
                                  isActive
                                    ? (darkMode ? "bg-teal-500/8 text-teal-400/40 cursor-not-allowed border border-teal-500/15" : "bg-teal-50/60 text-teal-400 cursor-not-allowed border border-teal-100")
                                    : (darkMode ? "bg-white/[0.03] text-gray-300 hover:bg-teal-500/15 hover:text-teal-300 border border-white/[0.06] hover:border-teal-500/25" : "bg-white/60 text-gray-700 hover:bg-teal-50 hover:text-teal-700 border border-gray-200/60 hover:border-teal-200")
                                }`}
                                whileHover={!isActive ? { scale: 1.05 } : {}}
                                whileTap={!isActive ? { scale: 0.95 } : {}}
                              >
                                {c}
                                {isActive && <span className="ml-1 opacity-50">·在線</span>}
                              </motion.button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {studioPersonnel.regularPersonnel.length === 0 && studioPersonnel.companions.length === 0 && (
                      <p className={`text-[10px] sm:text-xs ${darkMode ? "text-gray-600" : "text-gray-400"}`}>
                        暫無預設人員名單
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6, duration: 0.5 }}
        className={`mt-6 sm:mt-8 text-[10px] sm:text-xs tracking-widest uppercase ${darkMode ? "text-gray-600" : "text-gray-400"}`}
      >
        © {new Date().getFullYear()} XBER · v1.0.2
      </motion.div>

      {/* 快捷選擇確認視窗 */}
      <AnimatePresence>
        {confirmAction && (
          <motion.div
            className="fixed inset-0 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-md z-50 px-0 sm:px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setConfirmAction(null)}
          >
            <motion.div
              className={`w-full sm:max-w-sm p-5 sm:p-6 rounded-t-2xl sm:rounded-2xl shadow-2xl ${
                darkMode ? "bg-[#1a1a1a] border border-white/[0.08]" : "bg-white border border-gray-100"
              }`}
              initial={{ scale: 0.95, opacity: 0, y: 40 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 40 }}
              transition={{ type: "spring", stiffness: 350, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-8 h-1 rounded-full bg-gray-300/30 mx-auto mb-4 sm:hidden" />
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-2.5 rounded-xl ${
                  confirmAction.type === 'regular'
                    ? (darkMode ? "bg-emerald-500/10 border border-emerald-500/20" : "bg-emerald-50 border border-emerald-100")
                    : (darkMode ? "bg-teal-500/10 border border-teal-500/20" : "bg-teal-50 border border-teal-100")
                }`}>
                  <AlertTriangle className={`w-5 h-5 ${
                    confirmAction.type === 'regular'
                      ? (darkMode ? "text-emerald-400" : "text-emerald-600")
                      : (darkMode ? "text-teal-400" : "text-teal-600")
                  }`} />
                </div>
                <div>
                  <h3 className={`text-sm sm:text-base font-bold tracking-tight ${darkMode ? "text-white" : "text-gray-800"}`}>
                    確認進入工作室
                  </h3>
                  <p className={`text-[10px] sm:text-xs mt-0.5 ${darkMode ? "text-gray-500" : "text-gray-500"}`}>
                    {confirmAction.type === 'regular' ? '日常人員' : '隨行人員'}
                  </p>
                </div>
              </div>

              <div className={`p-3 rounded-xl mb-4 ${darkMode ? "bg-white/[0.03] border border-white/[0.05]" : "bg-gray-50/80 border border-gray-100"}`}>
                <p className={`text-xs sm:text-sm ${darkMode ? "text-gray-200" : "text-gray-700"}`}>
                  確定要讓 <span className={`font-bold ${
                    confirmAction.type === 'regular'
                      ? (darkMode ? "text-emerald-400" : "text-emerald-600")
                      : (darkMode ? "text-teal-400" : "text-teal-600")
                  }`}>{confirmAction.personName}</span> 進入工作室嗎？
                </p>
                <p className={`text-[10px] sm:text-xs mt-1 ${darkMode ? "text-gray-600" : "text-gray-400"}`}>
                  目的：{confirmAction.type === 'regular' ? '工作' : '隨行'}
                </p>
              </div>

              <div className="flex gap-2">
                <motion.button
                  onClick={() => {
                    handleQuickSelect(confirmAction.personName, confirmAction.type);
                    setConfirmAction(null);
                  }}
                  className={`flex-1 py-3 rounded-xl text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                    confirmAction.type === 'regular'
                      ? "bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-[0_2px_12px_rgba(16,185,129,0.25)]"
                      : "bg-gradient-to-r from-teal-600 to-emerald-600 text-white shadow-[0_2px_12px_rgba(20,184,166,0.25)]"
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Check className="w-4 h-4" />
                  確認進入
                </motion.button>
                <motion.button
                  onClick={() => setConfirmAction(null)}
                  className={`px-4 py-3 rounded-xl text-xs sm:text-sm font-medium transition-all ${
                    darkMode ? "bg-white/[0.04] text-gray-400 hover:bg-white/[0.08] border border-white/[0.06]" : "bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-100"
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  取消
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 登入成功通知 */}
      <AnimatePresence>
        {showNotification && (
          <motion.div
            className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-md z-50 px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              className={`w-full max-w-[280px] sm:max-w-xs p-6 sm:p-8 rounded-2xl sm:rounded-3xl shadow-2xl flex flex-col items-center ${
                darkMode ? "bg-[#1a1a1a] border border-white/[0.08]" : "bg-white border border-gray-100"
              }`}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={springTransition}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1, transition: { delay: 0.2, ...springTransition } }}
              >
                <CheckCircle2 className="w-12 h-12 sm:w-16 sm:h-16 text-green-500 mb-3 sm:mb-4" />
              </motion.div>
              <h3
                className={`text-lg sm:text-xl font-bold mb-1.5 tracking-tight ${
                  darkMode ? "text-white" : "text-gray-800"
                }`}
              >
                登入成功
              </h3>
              <p
                className={`text-center text-xs sm:text-sm ${
                  darkMode ? "text-gray-500" : "text-gray-500"
                }`}
              >
                正在跳轉到主頁面...
              </p>
              <div className={`mt-4 relative w-36 sm:w-48 h-1 rounded-full overflow-hidden ${
                darkMode ? "bg-white/[0.06]" : "bg-gray-200"
              }`}>
                <motion.div
                  className="absolute top-0 left-0 h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: "100%" }}
                  transition={{ duration: 1.5, ease: "easeInOut" }}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* CSS 抖動效果 */}
      <style jsx>{`
        .shake {
          animation: shake 0.5s cubic-bezier(.36,.07,.19,.97) both;
        }
        
        @keyframes shake {
          10%, 90% { transform: translateX(-1px); }
          20%, 80% { transform: translateX(2px); }
          30%, 50%, 70% { transform: translateX(-4px); }
          40%, 60% { transform: translateX(4px); }
        }
      `}</style>
    </div>
  );
}

export default Login;